import React, { useCallback, useEffect, useState } from "react";
import { Upload, Space, Spin, Modal, message } from "antd";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";

const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
];
const MAX_SIZE = 10 * 1024 * 1024; // 10MB, same limit the Documents tab enforces

/**
 * Loads the employee's identity documents (identityinformation) WITHOUT
 * touching EmployeeDetails, so refreshing after an upload never resets the
 * unsaved values in the edit form.
 */
export const useIdentityDocs = (clientId, userId) => {
  const [docs, setDocs] = useState([]);

  const refresh = useCallback(async () => {
    if (!clientId || !userId) return;
    const result = await CALL_API(
      `getuserbyid?clientid=${clientId}&userid=${userId}`,
      "get"
    );
    if (result?.code === STATUS_CODE.SUCCESS) {
      setDocs(result?.response?.identityinformation || []);
    }
  }, [clientId, userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { docs, refresh };
};

/**
 * Attach / Re-attach / View / Delete control for one identity document.
 * identityKey matches the Documents tab: "nationalid", "krapin", "housingfundno",
 * "nssf", "nhif", "helpmonthlybilling".
 */
const IdentityDocUpload = ({ clientId, userId, identityKey, docs = [], onChanged }) => {
  const [busy, setBusy] = useState(false);

  const doc = docs.find((d) => d?.type === identityKey);
  const hasDoc = Boolean(doc?.documentId);

  const upload = async (file) => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      message.error("Only PDF, DOC, and PPT files are allowed");
      return;
    }
    if (file.size > MAX_SIZE) {
      message.error("You can only upload files up to 10MB in size.");
      return;
    }

    setBusy(true);
    try {
      // 1. get a pre-signed S3 url
      const res = await CALL_API(
        `upload-identity/${clientId}/${userId}?endpoint=peopledocuments`,
        "post",
        { filename: file.name }
      );
      if (!res?.url) {
        message.error("Could not start the upload.");
        return;
      }

      // 2. PUT the file to S3
      const putRes = await fetch(res.url, {
        method: "PUT",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      if (!putRes.ok) {
        message.error("File upload failed");
        return;
      }

      // 3. register the document against the employee
      const { code } = await CALL_API(
        `upload-identity/${clientId}/${userId}`,
        "post",
        {
          name: file.name,
          type: file.type,
          objectKey: res?.key || "",
          documentName: "identityinformation",
          documentType: identityKey,
        }
      );

      if (code === STATUS_CODE.SUCCESS) {
        message.success("Document uploaded");
        await onChanged?.();
      } else {
        message.error("You can only upload files up to 10MB in size.");
      }
    } catch (error) {
      console.error("[IdentityDocUpload] upload error", error);
      message.error("File upload failed");
    } finally {
      setBusy(false);
    }
  };

  const view = async () => {
    setBusy(true);
    try {
      const { code, url } = await CALL_API(`document-list/${clientId}`, "post", {
        method: "preview-identity-document",
        documentId: doc.documentId,
        identityName: "identityinformation",
        userId,
      });
      const resolvedURL =
        typeof url === "string"
          ? url
          : url?.publicurl || url?.url || url?.signedUrl || url?.fileUrl || "";
      if (code === STATUS_CODE.SUCCESS && resolvedURL) {
        window.open(resolvedURL, "_blank", "noopener");
      } else {
        message.error("Something went wrong!! Please try again.");
      }
    } catch (error) {
      message.error("Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    Modal.confirm({
      title: "Delete this document?",
      okText: "Yes",
      cancelText: "No",
      centered: true,
      onOk: async () => {
        setBusy(true);
        try {
          const { code } = await CALL_API(
            `upload-document/${clientId}/${doc.documentId}`,
            "delete",
            { documentName: "identityinformation", userId }
          );
          if (code === STATUS_CODE.SUCCESS) {
            message.success("Document has been Deleted");
            await onChanged?.();
          }
        } finally {
          setBusy(false);
        }
      },
    });
  };

  return (
    <Spin spinning={busy} size="small">
      <Space wrap size={8} style={{ marginTop: 6 }}>
        <Upload
          showUploadList={false}
          accept=".pdf,.doc,.docx,.ppt,.pptx"
          beforeUpload={(file) => {
            upload(file);
            return false; // we upload manually, stop antd's auto-upload
          }}
        >
          <ButtonFilled type="button">
            {hasDoc ? "Re-attach file" : "Attach file"}
          </ButtonFilled>
        </Upload>

        {hasDoc && (
          <>
            <button
              type="button"
              className="bg-transparent text-info border-0"
              onClick={view}
            >
              {doc.documentName}
            </button>
            <ButtonOutlined type="button" onClick={remove}>
              Delete
            </ButtonOutlined>
          </>
        )}
      </Space>
    </Spin>
  );
};

export default IdentityDocUpload;
